import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowUpRight,
  Briefcase,
  CalendarClock,
  CalendarOff,
  CheckCircle2,
  Circle,
  Compass,
  FileSignature,
  Gavel,
  ListTodo,
  MessageCircle,
  Phone,
  X,
} from 'lucide-react';
import { usePracticeStore } from '../store/practiceStore';
import { useAuthStore } from '../store/authStore';
import { useJobsStore } from '../store/jobsStore';
import { useDraftsStore } from '../store/draftsStore';
import { useMattersStore } from '../store/mattersStore';
import { BoardHearingItem, Draft, ResearchJob } from '../types';
import { HearingCard } from '../components/practice/HearingCard';
import { CallListRow } from '../components/practice/CallListRow';
import { TaskRow } from '../components/practice/TaskRow';
import { PracticeAssistant } from '../components/practice/PracticeAssistant';
import { dayLabel, fmtDay } from '../components/practice/format';
import { statusMeta } from '../lib/meta';
import { Badge, EmptyState, SkeletonCard } from '../components/ui';
import { useTourStore } from '../tours/tourStore';

const NUDGE_KEY = 'practice_wa_nudge_dismissed';

/** Trilingual example chips (§3.1) — every chip prefills the research intake. */
const EXAMPLE_CHIPS = [
  'Research anticipatory bail after a 498A FIR',
  'Draft a Section 138 demand notice',
  'New case — Suresh Menon vs KSEB, Ernakulam Munsiff, hearing 24 July',
];

/** "Example"-watermarked wrapper for the ghosted sample content (§3.1). */
function GhostCard({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={`relative bg-white rounded-card border border-dashed border-legal-200 opacity-70 select-none pointer-events-none ${className}`}
    >
      <span className="absolute top-2.5 right-2.5 text-[10px] uppercase tracking-wide font-semibold text-legal-300 border border-legal-200 rounded px-1.5 py-0.5">
        Example
      </span>
      {children}
    </div>
  );
}

/** One checklist row of the brand-new-user starter panel. */
function ChecklistItem({
  done,
  title,
  detail,
  to,
  track,
  primary,
}: {
  done: boolean;
  title: string;
  detail: string;
  to: string;
  track: string;
  primary?: boolean;
}) {
  return (
    <Link
      to={to}
      data-track={track}
      className={`flex items-start gap-3 rounded-xl border p-3.5 transition-colors press ${
        primary && !done
          ? 'bg-accent border-accent hover:bg-accent-400'
          : 'bg-white border-legal-200 hover:border-legal-300'
      }`}
    >
      {done ? (
        <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
      ) : (
        <Circle
          className={`h-5 w-5 shrink-0 mt-0.5 ${primary ? 'text-legal-900/60' : 'text-legal-300'}`}
        />
      )}
      <span className="min-w-0">
        <span
          className={`block text-sm font-semibold ${
            done ? 'text-legal-500 line-through' : primary ? 'text-legal-900' : 'text-legal-800'
          }`}
        >
          {title}
        </span>
        <span
          className={`block text-xs mt-0.5 ${primary && !done ? 'text-legal-800/80' : 'text-legal-400'}`}
        >
          {detail}
        </span>
      </span>
    </Link>
  );
}

/** Compact recent-work row — every row names its case (§5 connective tissue). */
function RecentRow({
  to,
  icon,
  title,
  caseName,
  date,
}: {
  to: string;
  icon: ReactNode;
  title: string;
  caseName?: string;
  date: string;
}) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 px-1 py-2.5 border-b border-legal-100 last:border-b-0"
    >
      {icon}
      <span className="min-w-0 flex-1">
        <span className="block text-sm text-legal-800 truncate group-hover:text-accent-700 transition-colors">
          {title}
        </span>
        {caseName && (
          <span className="flex items-center gap-1 text-[11px] text-legal-400 truncate">
            <Briefcase className="h-3 w-3 shrink-0" />
            {caseName}
          </span>
        )}
      </span>
      <span className="flex items-center gap-1.5 text-xs text-legal-400 shrink-0">
        {date}
        <ArrowUpRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-accent-600" />
      </span>
    </Link>
  );
}

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });

/**
 * Today (`/app`, spec §3) — the morning cause list, not a launcher. Today's
 * hearings, what needs an update, tomorrow's call list, due tasks and recent
 * research/drafts, with the assistant docked at the bottom. Built for a phone
 * outside a courtroom: one column, big targets. Brand-new users get the §3.1
 * starter checklist + ghosted sample instead of empty grey boxes.
 */
export function TodayPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { board, boardFetchedAt, fetchBoard } = usePracticeStore();
  const { jobs, lastFetched: jobsFetchedAt, fetchJobs } = useJobsStore();
  const { drafts, lastFetched: draftsFetchedAt, fetchDrafts } = useDraftsStore();
  const { matters, lastFetched: mattersFetchedAt, fetchMatters } = useMattersStore();
  const requestTour = useTourStore((s) => s.request);
  const [assistantScope, setAssistantScope] = useState<{ hearingId: string; opener: string } | null>(null);
  const [nudgeDismissed, setNudgeDismissed] = useState(
    () => localStorage.getItem(NUDGE_KEY) === '1'
  );
  const assistantRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchBoard();
    fetchJobs();
    fetchDrafts();
    fetchMatters();
  }, [fetchBoard, fetchJobs, fetchDrafts, fetchMatters]);

  const caseTitleById = useMemo(() => {
    const map = new Map<string, string>();
    matters.forEach((m) => map.set(m.id, m.title));
    return map;
  }, [matters]);

  const onLogOutcome = (h: BoardHearingItem) => {
    setAssistantScope({
      hearingId: h.hearing_id,
      opener: `Logging the outcome for ${h.case_title} (${dayLabel(h.hearing_date)}${
        h.purpose ? ` — ${h.purpose}` : ''
      }). What happened?`,
    });
    // The assistant is docked below — bring it into view.
    setTimeout(() => assistantRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 50);
  };

  const dismissNudge = () => {
    localStorage.setItem(NUDGE_KEY, '1');
    setNudgeDismissed(true);
  };

  // ---- Brand-new-user detection (§3.1): no cases AND no jobs AND no drafts.
  // A non-empty board (e.g. hearings shared by a collaborator) always wins —
  // never hide real court work behind the starter panel.
  const boardQuiet =
    !board ||
    board.today_hearings.length +
      board.tomorrow_hearings.length +
      board.needs_update.length +
      board.call_list.length +
      board.no_date_cases.length +
      board.tasks_due.length +
      board.assigned_to_me.length ===
      0;
  const hasJobs = jobs.length > 0;
  const hasCases = matters.length > 0;
  const hasDrafts = drafts.length > 0;
  const looksBrandNew = !hasJobs && !hasCases && !hasDrafts && boardQuiet;
  const dataReady =
    jobsFetchedAt !== null && draftsFetchedAt !== null && mattersFetchedAt !== null;
  const isBrandNew = looksBrandNew && dataReady;
  // Hold the skeleton while a possibly-brand-new user's data is still loading,
  // so the page never flashes empty sections before the starter panel.
  const isFirstLoad = (!board && boardFetchedAt === null) || (looksBrandNew && !dataReady);

  const checklistDone = [hasJobs, hasCases, !!user?.phone_verified].filter(Boolean).length;

  const jobsLeft = user ? Math.max(0, user.monthly_jobs_limit - user.monthly_jobs_used) : null;

  const todayStr = new Date().toLocaleDateString('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <div className="p-2 sm:p-6 max-w-3xl mx-auto">
      {/* Header — title + date left, quota meter + calendar shortcut right (§3) */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl font-medium text-legal-900">Today</h1>
          <p className="text-legal-500 mt-1 text-[15px]">
            {todayStr}
            {board?.as_of && (
              <span className="text-legal-400">
                {' · '}as of{' '}
                {new Date(board.as_of).toLocaleTimeString('en-IN', {
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </span>
            )}
          </p>
        </div>
        <div className="sm:text-right shrink-0">
          {user && jobsLeft !== null && (
            <p data-tour="quota-meter" className="text-sm font-medium text-legal-700">
              {jobsLeft} of {user.monthly_jobs_limit} researches left this month
            </p>
          )}
          <Link
            to="/app/settings#reminders"
            data-track="today-calendar-inbox"
            className="inline-flex items-center gap-1.5 text-xs text-legal-500 hover:text-legal-700 transition-colors mt-1"
          >
            <CalendarClock className="h-3.5 w-3.5" />
            Get hearings in your calendar or inbox
          </Link>
        </div>
      </div>

      {/* WhatsApp verification nudge (filled state only — the starter checklist does this job) */}
      {!isBrandNew && user && !user.phone_verified && !nudgeDismissed && (
        <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-card p-4 mb-6">
          <MessageCircle className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
          <p className="text-sm text-emerald-800 flex-1 min-w-0">
            Verify your WhatsApp number to get hearing reminders.{' '}
            <Link to="/app/settings" className="font-semibold underline hover:no-underline">
              Verify in Settings →
            </Link>
          </p>
          <button
            type="button"
            onClick={dismissNudge}
            aria-label="Dismiss reminder nudge"
            className="p-1 rounded-md text-emerald-600 hover:bg-emerald-100 transition-colors shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {isFirstLoad ? (
        <div className="space-y-4">
          <SkeletonCard lines={2} />
          <SkeletonCard lines={2} />
          <SkeletonCard lines={3} />
        </div>
      ) : isBrandNew ? (
        /* ---- §3.1 brand-new-user starter: What / Why / Next ---- */
        <div className="space-y-8 pb-4">
          <section className="bg-white rounded-card border border-legal-200 p-5">
            <div className="flex items-baseline justify-between gap-3 mb-1">
              <h2 className="font-semibold text-legal-900">
                This is Today — your morning cause list
              </h2>
              <span className="text-xs font-medium text-legal-400 tabular-nums shrink-0">
                {checklistDone} of 3 done
              </span>
            </div>
            <p className="text-sm text-legal-500 mb-2">
              Every hearing, client call and task lands here each morning. Three steps to make
              it yours:
            </p>
            {/* Primary first-login tour offer — checklist-launched (tour spec §2.1) */}
            <button
              type="button"
              data-track="today-tour-start"
              onClick={() => requestTour('around', '/app')}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-700 hover:text-accent-800 transition-colors mb-4"
            >
              <Compass className="h-4 w-4" />
              Show me around (2 minutes)
            </button>
            <div className="space-y-2.5">
              <ChecklistItem
                done={hasJobs}
                primary
                title="Ask your first legal question"
                detail="A full cited memorandum from one question — your free plan includes 3 researches a month."
                to="/app/research/new"
                track="today-checklist-research"
              />
              <ChecklistItem
                done={hasCases}
                title="Open your first case file"
                detail="Hearings, documents, research and drafts in one place."
                to="/app/cases"
                track="today-checklist-case"
              />
              <ChecklistItem
                done={!!user?.phone_verified}
                title="Get hearing reminders on WhatsApp"
                detail="Verify your number once — reminders arrive by email and in-app now, on WhatsApp once verified."
                to="/app/settings"
                track="today-checklist-reminders"
              />
            </div>
          </section>

          {/* Ghosted sample Today — show, don't tour (§3.1) */}
          <section data-tour="today-hearings">
            <h2 className="font-semibold text-legal-900 mb-3">How Today will look</h2>
            <div className="space-y-3">
              <GhostCard className="p-4">
                <p className="font-medium text-legal-900 pr-16">Suresh Menon vs KSEB</p>
                <p className="text-xs text-legal-400 mt-0.5">
                  Ernakulam Munsiff Court · O.S. 412/2026
                </p>
                <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                  <Badge tone="info">Evidence</Badge>
                  <Badge tone="success">Verified</Badge>
                </div>
              </GhostCard>
              <GhostCard className="flex items-center gap-3 px-4 py-3">
                <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
                <span className="min-w-0 flex-1 text-sm text-legal-800 truncate pr-14">
                  Anticipatory bail after a 498A FIR — Kerala
                </span>
              </GhostCard>
            </div>
          </section>

          {/* Trilingual example chips above the assistant composer (§3.1) */}
          <section>
            <p className="text-xs text-legal-500 mb-2">
              Try one — tap to see how it works. English, हिंदी and മലയാളം all work:
            </p>
            <div className="flex flex-wrap gap-2">
              {EXAMPLE_CHIPS.map((chip, i) => (
                <button
                  key={chip}
                  type="button"
                  data-track={`today-example-chip-${i + 1}`}
                  onClick={() => navigate('/app/research/new', { state: { prefill: chip } })}
                  className="text-left text-sm text-legal-700 bg-white border border-legal-200 rounded-full px-3.5 py-2 hover:border-accent hover:text-accent-700 transition-colors press"
                >
                  {chip}
                </button>
              ))}
            </div>
          </section>
        </div>
      ) : (
        <div className="space-y-8 pb-4">
          {/* Today's hearings */}
          <section data-tour="today-hearings">
            <h2 className="font-semibold text-legal-900 mb-3">Today's hearings</h2>
            {!board || board.today_hearings.length === 0 ? (
              <EmptyState icon={Gavel} message="No hearings today." />
            ) : (
              <div className="space-y-3 stagger-children">
                {board.today_hearings.map((h) => (
                  <HearingCard key={h.hearing_id} item={h} onLogOutcome={onLogOutcome} />
                ))}
              </div>
            )}
          </section>

          {/* Needs update — past hearings with no outcome logged */}
          {board && board.needs_update.length > 0 && (
            <section>
              <div className="bg-amber-50 border border-amber-200 rounded-card p-4">
                <h2 className="flex items-center gap-2 font-semibold text-amber-800 mb-1">
                  <AlertTriangle className="h-4 w-4" /> Needs update
                </h2>
                <p className="text-xs text-amber-700 mb-3">
                  These hearings passed without an outcome — log what happened so the diary stays true.
                </p>
                <div className="space-y-3 stagger-children">
                  {board.needs_update.map((h) => (
                    <HearingCard key={h.hearing_id} item={h} onLogOutcome={onLogOutcome} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Tomorrow */}
          <section>
            <h2 className="font-semibold text-legal-900 mb-3">Tomorrow</h2>
            {!board || board.tomorrow_hearings.length === 0 ? (
              <p className="text-sm text-legal-400 bg-white rounded-card border border-legal-200 p-4">
                Nothing listed for tomorrow.
              </p>
            ) : (
              <div className="space-y-3 stagger-children">
                {board.tomorrow_hearings.map((h) => (
                  <HearingCard key={h.hearing_id} item={h} />
                ))}
              </div>
            )}
          </section>

          {/* Call list */}
          {board && board.call_list.length > 0 && (
            <section>
              <h2 className="flex items-center gap-2 font-semibold text-legal-900 mb-1">
                <Phone className="h-4 w-4 text-legal-500" /> Call list
              </h2>
              <p className="text-sm text-legal-500 mb-3">
                Clients with a hearing tomorrow — one tap to call or WhatsApp.
              </p>
              <div className="bg-white rounded-card border border-legal-200 divide-y divide-legal-100 stagger-children">
                {board.call_list.map((c, i) => (
                  <CallListRow key={`${c.matter_id}-${i}`} item={c} />
                ))}
              </div>
            </section>
          )}

          {/* No next date */}
          {board && board.no_date_cases.length > 0 && (
            <section>
              <h2 className="flex items-center gap-2 font-semibold text-legal-900 mb-3">
                <CalendarOff className="h-4 w-4 text-legal-500" /> No next date
              </h2>
              <div className="bg-white rounded-card border border-legal-200 divide-y divide-legal-100">
                {board.no_date_cases.map((c) => (
                  <Link
                    key={c.matter_id}
                    to={`/app/cases/${c.matter_id}`}
                    className="flex items-center justify-between gap-3 p-3 hover:bg-legal-50 transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-sm text-legal-800 truncate">{c.case_title}</p>
                      {c.court_name && (
                        <p className="text-[11px] text-legal-400 truncate">{c.court_name}</p>
                      )}
                    </div>
                    {c.last_hearing_date && (
                      <span className="text-xs text-legal-400 shrink-0">
                        Last: {fmtDay(c.last_hearing_date)}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Tasks */}
          {board && (board.tasks_due.length > 0 || board.assigned_to_me.length > 0) && (
            <section>
              <h2 className="flex items-center gap-2 font-semibold text-legal-900 mb-3">
                <ListTodo className="h-4 w-4 text-legal-500" /> Tasks
              </h2>
              {board.tasks_due.length > 0 && (
                <>
                  <p className="text-xs uppercase tracking-wide text-legal-400 mb-1.5">Due</p>
                  <div className="bg-white rounded-card border border-legal-200 divide-y divide-legal-100 mb-4">
                    {board.tasks_due.map((t) => (
                      <TaskRow key={t.id} task={t} onChanged={() => fetchBoard()} />
                    ))}
                  </div>
                </>
              )}
              {board.assigned_to_me.length > 0 && (
                <>
                  <p className="text-xs uppercase tracking-wide text-legal-400 mb-1.5">
                    Assigned to me
                  </p>
                  <div className="bg-white rounded-card border border-legal-200 divide-y divide-legal-100">
                    {board.assigned_to_me.map((t) => (
                      <TaskRow key={t.id} task={t} onChanged={() => fetchBoard()} />
                    ))}
                  </div>
                </>
              )}
            </section>
          )}

          {/* Recent work — last 5 memos and drafts, each naming its case (§3 #6) */}
          <section>
            <h2 className="font-semibold text-legal-900 mb-3">Recent work</h2>
            <div className="bg-white rounded-card border border-legal-200 p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <div>
                  <div className="flex items-baseline justify-between gap-3 mb-1">
                    <p className="text-xs uppercase tracking-wide text-legal-400">Research</p>
                    <Link
                      to="/app/research"
                      data-track="today-all-research"
                      className="text-xs font-medium text-accent-700 hover:underline shrink-0"
                    >
                      All research →
                    </Link>
                  </div>
                  {jobs.length === 0 ? (
                    <p className="text-sm text-legal-400 py-2">
                      No research yet — your first memo is minutes away.
                    </p>
                  ) : (
                    jobs.slice(0, 5).map((job: ResearchJob) => (
                      <RecentRow
                        key={job.id}
                        to={`/app/research/${job.id}`}
                        icon={
                          <span
                            className={`h-2 w-2 rounded-full shrink-0 ${statusMeta(job.status).dot}`}
                            aria-label={statusMeta(job.status).label}
                          />
                        }
                        title={job.title}
                        caseName={job.matter_id ? caseTitleById.get(job.matter_id) : undefined}
                        date={shortDate(job.created_at)}
                      />
                    ))
                  )}
                </div>
                <div>
                  <div className="flex items-baseline justify-between gap-3 mb-1">
                    <p className="text-xs uppercase tracking-wide text-legal-400">Drafts</p>
                    <Link
                      to="/app/drafts"
                      data-track="today-all-drafts"
                      className="text-xs font-medium text-accent-700 hover:underline shrink-0"
                    >
                      All drafts →
                    </Link>
                  </div>
                  {drafts.length === 0 ? (
                    <p className="text-sm text-legal-400 py-2">
                      No drafts yet — the first one takes about a minute.
                    </p>
                  ) : (
                    drafts.slice(0, 5).map((draft: Draft) => (
                      <RecentRow
                        key={draft.id}
                        to={`/app/drafts/${draft.id}`}
                        icon={<FileSignature className="h-3.5 w-3.5 text-legal-400 shrink-0" />}
                        title={draft.title}
                        caseName={
                          draft.matter_id ? caseTitleById.get(draft.matter_id) : undefined
                        }
                        date={shortDate(draft.created_at)}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* Docked assistant — pre-scoped when "Log outcome" was tapped */}
      <div ref={assistantRef} className="sticky bottom-2 lg:bottom-4 z-30 mt-6">
        <PracticeAssistant
          key={assistantScope?.hearingId ?? 'board'}
          hearingId={assistantScope?.hearingId}
          opener={assistantScope?.opener}
          autoExpand={!!assistantScope}
        />
      </div>
    </div>
  );
}
